import { FeaturePageSection } from "@/features/feature-page/feature-page-section";
import { HeroBlockVisual } from "@/features/feature-page/hero-block-visual";
import { cn } from "@/lib/utils";
import type { ResolvedFeaturePageBlock } from "@garimpa/api/featurePages/types";

function heroPresentationClasses(compact: boolean) {
  if (compact) {
    return {
      body: "mt-5 text-base sm:text-lg",
      chips: "mt-5",
      inner: undefined,
      section: undefined,
      spacing: "py-14 sm:py-16 md:py-20",
      title: "text-4xl sm:text-6xl lg:text-7xl",
    };
  }
  return {
    body: "mt-6 text-lg sm:text-xl",
    chips: "mt-7",
    inner: "min-h-[60vh]",
    section: "min-h-[60vh]",
    spacing: undefined,
    title: "text-5xl sm:text-7xl lg:text-8xl",
  };
}

export function HeroBlock({
  block,
  compact = false,
}: {
  block: Extract<ResolvedFeaturePageBlock, { type: "hero" }>;
  compact?: boolean;
}) {
  const split = block.variant === "split";
  const presentation = heroPresentationClasses(compact);
  return (
    <FeaturePageSection
      block={block}
      labelledById={`${block.id}-heading`}
      resolvedBackground={block.resolvedBackground}
      priorityBackground
      spacingClassName={presentation.spacing}
      className={cn(
        "bg-[radial-gradient(circle_at_top,rgba(255,120,66,0.12),transparent_55%)]",
        presentation.section,
      )}
      innerClassName={cn(
        "flex items-center",
        presentation.inner,
        split ? "grid gap-10 lg:grid-cols-[1.15fr_0.85fr]" : "flex-col justify-center text-center",
      )}
    >
      <div className={cn(!split && "mx-auto max-w-5xl")}>
        {block.eyebrow ? (
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-primary sm:text-sm">
            {block.eyebrow}
          </p>
        ) : null}
        <h1
          id={`${block.id}-heading`}
          className={cn("text-balance font-bold leading-[0.98] tracking-tight", presentation.title)}
        >
          {block.title}
        </h1>
        <p
          className={cn(
            "max-w-3xl text-balance leading-relaxed text-muted-foreground",
            presentation.body,
            !split && "mx-auto",
          )}
        >
          {block.body}
        </p>
        {block.chips?.length ? (
          <ul
            className={cn("flex flex-wrap gap-2", presentation.chips, !split && "justify-center")}
          >
            {block.chips.map((chip) => (
              <li
                key={chip}
                className="rounded-full border border-border bg-background/70 px-3 py-1 text-sm backdrop-blur"
              >
                {chip}
              </li>
            ))}
          </ul>
        ) : null}
        {block.cta ? (
          <a
            href={block.cta.href}
            className="mt-8 inline-flex min-h-12 items-center rounded-lg bg-primary px-6 font-bold text-primary-foreground hover:bg-primary/90"
          >
            {block.cta.label}
          </a>
        ) : null}
      </div>
      <HeroBlockVisual block={block} split={split} />
    </FeaturePageSection>
  );
}
